import { useSessionStore } from "@auth/sessionStore";
import { describeCron } from "@recurrences/cadence";
import type {
  Recurrence,
  RecurrenceInput,
} from "@recurrences/recurrencesClient";
import {
  useCreateRecurrence,
  useUpdateRecurrence,
} from "@recurrences/recurrencesQueries";
import {
  Button,
  Clipboard,
  FieldError,
  Input,
  Select,
  Textarea,
} from "@scribenote/brian-design-system";
import { useState } from "react";

type CadenceType = "interval" | "cron";

const CADENCE_OPTIONS: { label: string; value: CadenceType }[] = [
  { label: "Fixed interval", value: "interval" },
  { label: "Cron expression", value: "cron" },
];

/** ISO instant → a `datetime-local` value (local time, minute precision). */
const toLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

/** A `datetime-local` value → an ISO instant, or null when empty. */
const fromLocalInput = (local: string): string | null =>
  local ? new Date(local).toISOString() : null;

const prettyPayload = (payload: unknown): string => {
  if (payload == null) return "";
  if (typeof payload === "string") return payload;
  return JSON.stringify(payload, null, 2);
};

/** Parse textarea content back to a payload: JSON when valid, raw string otherwise. */
const parsePayload = (text: string): unknown => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return text;
  }
};

/**
 * The recurrence form as a clipboard handed over: a standing instruction for
 * Brian, written down once and left to run. It takes the whole desk because
 * every field on it has to be settled before the schedule means anything.
 */
export const RecurrenceFormModal = ({
  recurrence,
  isOpen,
  onClose,
}: {
  /** The recurrence to edit, or undefined to create a new one. */
  recurrence?: Recurrence;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const clinicUuid = useSessionStore((s) => s.clinic?.clinicUuid ?? null);
  const create = useCreateRecurrence();
  const update = useUpdateRecurrence();

  const [title, setTitle] = useState(recurrence?.title ?? "");
  const [cadenceType, setCadenceType] = useState<CadenceType>(
    recurrence?.cron ? "cron" : "interval",
  );
  const [intervalMinutes, setIntervalMinutes] = useState(
    recurrence?.intervalMinutes != null
      ? String(recurrence.intervalMinutes)
      : "",
  );
  const [cron, setCron] = useState(recurrence?.cron ?? "");
  const [payloadText, setPayloadText] = useState(
    prettyPayload(recurrence?.payload),
  );
  const [startsAt, setStartsAt] = useState(
    toLocalInput(recurrence?.startsAt ?? null),
  );
  const [endsAt, setEndsAt] = useState(
    toLocalInput(recurrence?.endsAt ?? null),
  );
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  const buildInput = (): RecurrenceInput | null => {
    if (!title.trim()) {
      setError("Give the recurrence a title.");
      return null;
    }

    const payload = parsePayload(payloadText);

    if (cadenceType === "interval") {
      const minutes = Number(intervalMinutes);
      if (!Number.isInteger(minutes) || minutes <= 0) {
        setError("Interval must be a whole number of minutes greater than 0.");
        return null;
      }
      return {
        title: title.trim(),
        payload,
        intervalMinutes: minutes,
        cron: null,
        startsAt: fromLocalInput(startsAt),
        endsAt: fromLocalInput(endsAt),
      };
    }

    if (!cron.trim()) {
      setError("Enter a cron expression.");
      return null;
    }
    return {
      title: title.trim(),
      payload,
      intervalMinutes: null,
      cron: cron.trim(),
      startsAt: fromLocalInput(startsAt),
      endsAt: fromLocalInput(endsAt),
    };
  };

  const handleSubmit = () => {
    setError(null);
    const input = buildInput();
    if (!input) return;

    if (recurrence) {
      update.mutate(
        { id: recurrence.id, input },
        {
          onSuccess: onClose,
          onError: () => setError("Couldn’t save. Try again."),
        },
      );
      return;
    }

    if (!clinicUuid) {
      setError("No active clinic session.");
      return;
    }
    create.mutate(
      { input, about: [clinicUuid] },
      {
        onSuccess: onClose,
        onError: () => setError("Couldn’t create. Try again."),
      },
    );
  };

  return (
    <Clipboard
      open={isOpen}
      onClose={onClose}
      // Dropped while closed: a native <dialog> stays mounted, so an
      // unconditional testid would report an open form that isn't there.
      data-testid={isOpen ? "recurrence-form-modal" : undefined}
      title={recurrence ? "Edit recurrence" : "New recurrence"}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="recurrence-form-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            busy={pending}
            data-testid="recurrence-form-save"
          >
            {pending ? "Saving" : "Save"}
          </Button>
        </>
      }
    >
      {/* Reading width, centred on the sheet: a form is written down a column,
          not stretched across the whole board. */}
      <div className="mx-auto flex max-w-[38rem] flex-col gap-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Nightly inventory sweep"
          data-testid="recurrence-title"
        />

        <Select
          data-testid="recurrence-cadence-type"
          label="Cadence"
          options={CADENCE_OPTIONS}
          value={cadenceType}
          onValueChange={(value) => setCadenceType(value as CadenceType)}
        />

        {cadenceType === "interval" ? (
          <Input
            label="Every (minutes)"
            type="number"
            min={1}
            step={1}
            value={intervalMinutes}
            onChange={(e) => setIntervalMinutes(e.target.value)}
            placeholder="e.g. 60"
            data-testid="recurrence-interval"
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <Input
              label="Cron expression"
              value={cron}
              onChange={(e) => setCron(e.target.value)}
              placeholder="e.g. 0 2 * * *"
              data-testid="recurrence-cron"
            />
            {describeCron(cron) && (
              <p
                className="text-sm text-annotation"
                data-testid="recurrence-cron-description"
              >
                {describeCron(cron)}
              </p>
            )}
          </div>
        )}

        <Input
          label="Starts at (optional)"
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          data-testid="recurrence-starts-at"
        />

        <Input
          label="Ends at (optional)"
          type="datetime-local"
          value={endsAt}
          onChange={(e) => setEndsAt(e.target.value)}
          data-testid="recurrence-ends-at"
        />

        <Textarea
          id="recurrence-payload"
          label="Prompt"
          value={payloadText}
          onChange={(e) => setPayloadText(e.target.value)}
          rows={10}
          placeholder="Put instructions for what you want Brian to do here. For example:

Send me a notification for all the patients I've seen today with notes that haven't been approved or exported to the PIMS.
"
          className="font-mono"
          data-testid="recurrence-payload"
        />

        <FieldError>{error}</FieldError>
      </div>
    </Clipboard>
  );
};
